/**
 * Consent persistence: lightweight cookie + localStorage state, expiry, and
 * Global Privacy Control detection. Browser-only; all access is SSR-guarded.
 */

import {
  CONSENT_COOKIE_NAME,
  CONSENT_TTL_DAYS,
  type ConsentCategories,
  type ConsentDecision,
  type ConsentState,
  defaultCategories,
} from "./consent-categories";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** True when the browser advertises Global Privacy Control (Sec-GPC: 1). */
export function isGpcEnabled(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  );
}

export function isExpired(state: ConsentState, now: number = Date.now()): boolean {
  const decided = Date.parse(state.decidedAt);
  if (Number.isNaN(decided)) return true;
  return now - decided > CONSENT_TTL_DAYS * ONE_DAY_MS;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, maxAgeDays: number): void {
  if (typeof document === "undefined") return;
  const maxAge = Math.floor((maxAgeDays * ONE_DAY_MS) / 1000);
  const secure =
    typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is async; consent must persist synchronously before tracker init.
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`;
}

function deleteCookie(name: string): void {
  if (typeof document === "undefined") return;
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is async; consent reset must be synchronous on signout.
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
}

/** Reads stored consent. Returns null when absent or expired (forces re-prompt). */
export function readConsent(now: number = Date.now()): ConsentState | null {
  const raw = readCookie(CONSENT_COOKIE_NAME);
  if (!raw) return null;
  try {
    const state = JSON.parse(raw) as ConsentState;
    if (isExpired(state, now)) return null;
    return state;
  } catch {
    return null;
  }
}

export function writeConsent(
  categories: ConsentCategories,
  decision: ConsentDecision,
  policyVersion: string,
  now: number = Date.now()
): ConsentState {
  const state: ConsentState = {
    categories,
    decision,
    decidedAt: new Date(now).toISOString(),
    policyVersion,
  };
  writeCookie(CONSENT_COOKIE_NAME, JSON.stringify(state), CONSENT_TTL_DAYS);
  return state;
}

export function clearConsent(): void {
  deleteCookie(CONSENT_COOKIE_NAME);
}

/**
 * Returns the stored categories, or default-denied when nothing is stored.
 * Pure: GPC is applied by callers (instrumentation + provider), not here.
 */
export function resolveInitialCategories(stored: ConsentState | null): ConsentCategories {
  if (stored) return stored.categories;
  return defaultCategories();
}
